import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Tag, Steps, Descriptions, Table, Empty, Popconfirm, Tooltip, Space, Row, Col, Spin } from 'antd';
import {
  FileTextOutlined, FileDoneOutlined, BankOutlined, TruckOutlined,
  CheckCircleOutlined, CloseCircleOutlined, ReloadOutlined, DollarOutlined,
  FilePdfOutlined, ArrowLeftOutlined, ShoppingCartOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { formatDate, formatMontant } from '../../variables/constants';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import {
  getCommande, listerFacturesParCommande, listerChequesParCommande, listerTranchesParCommande,
  genererProforma, genererFactureDefinitive, annulerCommande, solderCommande,
  encaisserCheque, annulerCheque, ouvrirFacturePdf,
  STATUTS_COMMANDE, STATUTS_CHEQUE_ATT, TYPES_FACTURE,
} from '../../services/attestationsService';
import EnregistrerChequeModal from './modals/EnregistrerChequeModal';
import LivrerTrancheModal from './modals/LivrerTrancheModal';
import { useTranslation } from 'react-i18next';
import { avecLibelleTraduit } from '../../i18n/libelles';

const STEPS = avecLibelleTraduit([
  { key: 'PROFORMA_EMISE', icon: <FileTextOutlined /> },
  { key: 'CHEQUE_RECU',    icon: <BankOutlined /> },
  { key: 'LIVRE',          icon: <TruckOutlined /> },
  { key: 'FACTURE_EMISE',  icon: <FileDoneOutlined /> },
  { key: 'SOLDE',          icon: <CheckCircleOutlined /> },
], 'attestations:detail.etapes', { cle: 'key', champ: 'titre' });

/**
 * Détail d'une commande d'attestations Carte Brune — page à part entière.
 *
 * C'était un tiroir latéral ouvert par-dessus la liste. Une page donne toute la
 * largeur aux quatre tableaux (factures, chèques, plages), une adresse que l'on
 * peut partager ou garder en favori, et un retour qui ramène à l'onglet d'où
 * l'on venait (Commandes ou Chèques).
 */
export default function DetailCommande() {
  const { t } = useTranslation('attestations');
  const { commandeId } = useParams();
  const navigate = useNavigate();
  const [commande, setCommande] = useState(null);
  const [factures, setFactures] = useState([]);
  const [cheques, setCheques] = useState([]);
  const [tranches, setTranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [chequeModal, setChequeModal] = useState(false);
  const [trancheModal, setTrancheModal] = useState(false);

  const load = useCallback(async () => {
    if (!commandeId) return;
    setLoading(true);
    try {
      const [c, f, ch, t] = await Promise.all([
        getCommande(commandeId),
        listerFacturesParCommande(commandeId).catch(() => []),
        listerChequesParCommande(commandeId).catch(() => []),
        listerTranchesParCommande(commandeId).catch(() => []),
      ]);
      setCommande(c);
      setFactures(f);
      setCheques(ch);
      setTranches(t);
    } finally {
      setLoading(false);
    }
  }, [commandeId]);

  useEffect(() => { load(); }, [load]);

  // Retour : l'écran précédent s'il est dans l'application (l'onglet actif est
  // porté par son URL), la liste des commandes sinon — lien ouvert directement.
  const retour = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate('/attestations');
  };
  const boutonRetour = (
    <Button icon={<ArrowLeftOutlined />} onClick={retour} style={{ borderRadius: 8 }}>
      {t('detail.retour')}
    </Button>
  );

  const quantiteLivree = useMemo(() => tranches.reduce((s, t) => s + (t.quantiteLivree || 0), 0), [tranches]);
  const quantiteRestante = (commande?.quantite || 0) - quantiteLivree;
  const proforma = factures.find(f => f.typeFacture === 'PROFORMA' && !f.annulee);
  const factureDefinitive = factures.find(f => f.typeFacture === 'DEFINITIVE' && !f.annulee);

  const stepIndex = useMemo(() => {
    if (!commande) return 0;
    if (commande.statut === 'ANNULEE') return -1;
    if (commande.statut === 'SOLDE') return 4;
    if (commande.statut === 'FACTURE_EMISE') return 3;
    if (commande.statut === 'LIVRE') return 2;
    if (commande.statut === 'CHEQUE_RECU') return 1;
    return 0;
  }, [commande]);

  const refresh = async (msg) => {
    if (msg) toast.success(msg);
    await load();
  };

  const action = async (fn, successMsg) => {
    setActing(true);
    try { await fn(); await refresh(successMsg); }
    catch (_) { /* intercepteur */ }
    finally { setActing(false); }
  };

  if (!commande) {
    return (
      <Layout title={t('detail.titreChargement')}>
        <PageHeader titre={t('detail.titreChargement')} icone={<ShoppingCartOutlined />} actions={boutonRetour} />
        <div className="cb-card" style={{ padding: 40, textAlign: 'center' }}>
          {loading ? <Spin /> : <Empty description={t('detail.introuvable')} />}
        </div>
      </Layout>
    );
  }

  const statutTag = STATUTS_COMMANDE[commande.statut] || { label: commande.statut, color: 'default' };
  const annulee = commande.statut === 'ANNULEE';
  const soldee = commande.statut === 'SOLDE';

  const colsFactures = [
    { title: t('commun.colonnes.numeroFacture'), dataIndex: 'numeroFacture', key: 'num',
      render: (v) => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 700, color: '#1a6b3c' }}>{v}</span> },
    { title: t('detail.colonnes.type'), dataIndex: 'typeFacture', key: 'type',
      render: (v) => <Tag color={TYPES_FACTURE[v]?.color}>{TYPES_FACTURE[v]?.label || v}</Tag> },
    { title: t('commun.colonnes.date'), dataIndex: 'dateFacture', key: 'date', render: formatDate },
    { title: t('detail.colonnes.echeance'), dataIndex: 'dateEcheance', key: 'ech', render: formatDate },
    { title: t('commun.colonnes.total'), dataIndex: 'montantTotal', key: 'tot', align: 'right',
      render: (v) => <strong>{formatMontant(v)}</strong> },
    { title: t('commun.colonnes.etat'), key: 'st', render: (_, f) => f.annulee ? <Tag color="red">{t('commun.annulee')}</Tag> : <Tag color="green">{t('commun.active')}</Tag> },
    { title: '', key: 'pdf', width: 90, render: (_, f) => (
      <Button size="small" icon={<FilePdfOutlined />}
        onClick={() => ouvrirFacturePdf(f.factureTrackingId)}
        style={{ color: '#b91c1c', borderColor: '#fecaca' }}>
        PDF
      </Button>
    ) },
  ];

  const colsCheques = [
    { title: t('commun.colonnes.numeroCheque'), dataIndex: 'numeroCheque', key: 'num',
      render: (v) => <span style={{ fontFamily: 'var(--font-main)', fontWeight: 600 }}>{v}</span> },
    { title: t('commun.colonnes.banque'), dataIndex: 'banqueEmettrice', key: 'banque', render: (v) => v || '—' },
    { title: t('detail.colonnes.emission'), dataIndex: 'dateEmission', key: 'em', render: formatDate },
    { title: t('detail.colonnes.encaissement'), dataIndex: 'dateEncaissement', key: 'enc', render: (v) => v ? formatDate(v) : '—' },
    { title: t('commun.colonnes.montant'), dataIndex: 'montant', key: 'mt', align: 'right', render: (v) => <strong>{formatMontant(v)}</strong> },
    { title: t('commun.colonnes.statut'), dataIndex: 'statut', key: 'st',
      render: (v) => {
        const s = STATUTS_CHEQUE_ATT[v] || { label: v, color: 'default' };
        return <Tag color={s.color}>{s.label}</Tag>;
      } },
    { title: '', key: 'act', render: (_, c) => (
      <Space size={4}>
        {c.statut === 'RECU' && (
          <Popconfirm title={t('commun.cheque.encaisserTitre')}
            description={t('commun.cheque.encaisserDescription')}
            onConfirm={() => action(() => encaisserCheque(c.chequeTrackingId, dayjs().format('YYYY-MM-DD')),
              t('commun.cheque.encaisse'))}>
            <Button size="small" type="primary" icon={<DollarOutlined />}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>{t('commun.cheque.encaisser')}</Button>
          </Popconfirm>
        )}
        {(c.statut === 'RECU' || c.statut === 'ENCAISSE') && (
          <Popconfirm title={t('commun.cheque.annulerTitre')}
            onConfirm={() => action(() => annulerCheque(c.chequeTrackingId, 'Annulation manuelle'), t('commun.cheque.annule'))}>
            <Button size="small" danger icon={<CloseCircleOutlined />}>{t('commun.annuler')}</Button>
          </Popconfirm>
        )}
      </Space>
    ) },
  ];

  const colsTranches = [
    { title: t('detail.colonnes.lot'), dataIndex: 'referenceLot', key: 'lot' },
    { title: t('detail.colonnes.plage'), key: 'plage', render: (_, t) => `${t.numeroDebutSerie} → ${t.numeroFinSerie}` },
    { title: t('commun.colonnes.quantite'), dataIndex: 'quantiteLivree', key: 'qty', align: 'right',
      render: (v) => <strong>{v}</strong> },
    { title: t('commun.colonnes.date'), dataIndex: 'dateLivraison', key: 'date', render: formatDate },
  ];

  return (
    <Layout title={t('detail.titre', { numero: commande.numeroCommande })}>
      <PageHeader
        titre={(
          <Space size={10}>
            <span>{commande.numeroCommande}</span>
            <Tag color={statutTag.color} style={{ borderRadius: 20, fontSize: 13 }}>{statutTag.label}</Tag>
          </Space>
        )}
        icone={<ShoppingCartOutlined />}
        precision={commande.organismeRaisonSociale}
        actions={(
          <Space>
            {boutonRetour}
            <Tooltip title={t('detail.rafraichir')}>
              <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 8 }} />
            </Tooltip>
            {!annulee && !soldee && (
              <Popconfirm title={t('detail.annulerCommandeTitre')}
                onConfirm={() => action(() => annulerCommande(commande.commandeTrackingId), t('detail.commandeAnnulee'))}>
                <Button danger icon={<CloseCircleOutlined />} style={{ borderRadius: 8 }}>{t('detail.annulerCommande')}</Button>
              </Popconfirm>
            )}
          </Space>
        )}
      />

      <div className="cb-card" style={{ marginBottom: 16 }}>
        {!annulee && (
          <Steps current={stepIndex >= 0 ? stepIndex : 0} size="small" style={{ marginBottom: 24 }}
            status={annulee ? 'error' : undefined}
            items={STEPS.map(s => ({ title: s.titre, icon: s.icon }))} />
        )}

        <Descriptions bordered size="small" column={{ xs: 1, md: 2 }} style={{ marginBottom: 20 }}>
          <Descriptions.Item label={t('commun.colonnes.compagnie')}>{commande.organismeRaisonSociale}</Descriptions.Item>
          <Descriptions.Item label={t('commun.colonnes.dateCommande')}>{formatDate(commande.dateCommande)}</Descriptions.Item>
          <Descriptions.Item label={t('commun.colonnes.quantite')}>{commande.quantite}</Descriptions.Item>
          <Descriptions.Item label={t('detail.champs.livraisonEffective')}>{commande.dateLivraisonEffective ? formatDate(commande.dateLivraisonEffective) : '—'}</Descriptions.Item>
          <Descriptions.Item label={t('detail.champs.prixUnitaire')}>{formatMontant(commande.prixUnitaireVente)}</Descriptions.Item>
          <Descriptions.Item label={t('detail.champs.beneficiaireCheque')}>{commande.nomBeneficiaireCheque || '—'}</Descriptions.Item>
          <Descriptions.Item label={t('detail.champs.sousTotal')}>{formatMontant(commande.montantAttestation)}</Descriptions.Item>
          <Descriptions.Item label={t('detail.champs.contributionFonds')}>{formatMontant(commande.montantContributionFonds)}</Descriptions.Item>
          <Descriptions.Item label={t('detail.champs.totalAPayer')} span={2}>
            <strong style={{ color: '#1a6b3c', fontSize: 16 }}>{formatMontant(commande.montantTotal)}</strong>
          </Descriptions.Item>
        </Descriptions>

        {/* Actions par étape */}
        <Row gutter={[12, 12]}>
          <Col>
            <Button type="primary" icon={<FileTextOutlined />}
              disabled={annulee || !!proforma}
              loading={acting}
              onClick={() => action(() => genererProforma(commande.commandeTrackingId), t('detail.actions.proformaGeneree'))}
              style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}>
              {proforma ? t('detail.actions.proformaNumero', { numero: proforma.numeroFacture }) : t('detail.actions.genererProforma')}
            </Button>
          </Col>
          <Col>
            <Button icon={<BankOutlined />}
              disabled={annulee || !proforma}
              onClick={() => setChequeModal(true)}>
              {t('detail.actions.enregistrerCheque')}
            </Button>
          </Col>
          <Col>
            <Tooltip title={!proforma ? t('detail.actions.genererProformaDabord') : ''}>
              <Button icon={<TruckOutlined />}
                disabled={annulee || !proforma || quantiteRestante <= 0}
                onClick={() => setTrancheModal(true)}>
                {t('detail.actions.attribuerPlage')}
                {quantiteRestante > 0 && quantiteRestante < commande.quantite && (
                  <span style={{ marginLeft: 6, color: '#92400e' }}>{t('detail.actions.reste', { n: quantiteRestante })}</span>
                )}
              </Button>
            </Tooltip>
          </Col>
          <Col>
            <Tooltip title={t('detail.actions.disponibleApres')}>
              <Button icon={<FileDoneOutlined />}
                disabled={annulee || !!factureDefinitive
                  || !(commande.statut === 'CHEQUE_RECU' || commande.statut === 'LIVRE')}
                loading={acting}
                onClick={() => action(() => genererFactureDefinitive(commande.commandeTrackingId), t('detail.actions.factureEmise'))}>
                {factureDefinitive ? t('detail.actions.factureNumero', { numero: factureDefinitive.numeroFacture }) : t('detail.actions.emettreFacture')}
              </Button>
            </Tooltip>
          </Col>
          <Col>
            <Popconfirm title={t('detail.actions.solderTitre')}
              description={t('detail.actions.solderDescription')}
              onConfirm={() => action(() => solderCommande(commande.commandeTrackingId), t('detail.actions.commandeSoldee'))}>
              <Button icon={<CheckCircleOutlined />}
                disabled={annulee || soldee || !(commande.statut === 'FACTURE_EMISE' || commande.statut === 'LIVRE')}>
                {t('detail.actions.solder')}
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </div>

      <div className="cb-card" style={{ marginBottom: 16 }}>
        <div className="cb-card-title" style={{ marginBottom: 12 }}>{t('detail.sections.factures', { n: factures.length })}</div>
        <Table className="cb-table" size="small" rowKey="factureTrackingId" dataSource={factures} columns={colsFactures}
          pagination={false} scroll={{ x: 800 }}
          locale={{ emptyText: <Empty description={t('detail.sections.aucuneFacture')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />
      </div>

      <div className="cb-card" style={{ marginBottom: 16 }}>
        <div className="cb-card-title" style={{ marginBottom: 12 }}>{t('detail.sections.chequesRecus', { n: cheques.length })}</div>
        <Table className="cb-table" size="small" rowKey="chequeTrackingId" dataSource={cheques} columns={colsCheques}
          pagination={false} scroll={{ x: 800 }}
          locale={{ emptyText: <Empty description={t('commun.aucunCheque')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />
      </div>

      <div className="cb-card" style={{ marginBottom: 16 }}>
        <div className="cb-card-title" style={{ marginBottom: 12 }}>
          {t('detail.sections.plages', { livree: quantiteLivree, total: commande.quantite })}
        </div>
        <Table className="cb-table" size="small" rowKey="trancheTrackingId" dataSource={tranches} columns={colsTranches}
          pagination={false}
          locale={{ emptyText: <Empty description={t('detail.sections.aucunePlage')} image={Empty.PRESENTED_IMAGE_SIMPLE} /> }} />
      </div>

      <EnregistrerChequeModal open={chequeModal} onClose={() => setChequeModal(false)}
        commandeId={commande.commandeTrackingId}
        facturePreselectionnee={proforma || factureDefinitive}
        onCreated={() => refresh()} />

      <LivrerTrancheModal open={trancheModal} onClose={() => setTrancheModal(false)}
        commande={commande}
        quantiteRestante={quantiteRestante}
        onCreated={() => refresh()} />
    </Layout>
  );
}
